function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/** @jsxRuntime classic */

/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import { useState, useRef, useEffect, memo } from 'react';
import Inputmask from 'inputmask';
import useOnDidUpdate from '../../hooks/use-on-didUpdate';
import { useDeepCompare } from '../../hooks/use-deep-compare-effect';
import { mediaQuery, getCursorPosition } from '../../utils';
import { migratoryWebCitizen, phytosanitaryWeb, sanitarianRegistersWeb, tdCarWeb } from '../Colors';
import DatesPicker from '../forms/inputs/DatePicker';
import styles from './InputsUI.style';

const titleStyle = ({
  theme = 'migratoryWebCitizen'
}) => {
  const customCSSByApp = {
    migratoryWebCitizen: css`
      color: ${migratoryWebCitizen.cerulean};
    `,
    sanitarianRegistersWeb: css`
      color: ${sanitarianRegistersWeb.cerulean};
    `,
    phytosanitaryWeb: css`
      color: ${phytosanitaryWeb.darkGreen};
    `,
    tdCarWeb: css`
      color: ${tdCarWeb.cerulean};
    `
  };
  return [css`
      font-size: 26px;
      text-transform: uppercase;
    `, customCSSByApp[theme]];
};
/**
 * This component represent a title.
 * @param {*} children What will be shown inside the h1 tag.
 * @param {*} props All other supported props.
 */


export function Title({
  children,
  ...props
}) {
  return jsx("h1", _extends({
    css: titleStyle
  }, props), children);
}
/**
 * This component represent a title sign up.
 * @param {*} children What will be shown inside the h1 tag.
 * @param {JSON} containerStyle Custom h1 styles.
 */

export const TitleSignup = /*#__PURE__*/memo(({
  children,
  containerStyle
}) => {
  return jsx("h1", {
    style: {
      fontSize: 26,
      color: '#0f7cb5',
      textTransform: 'uppercase',
      ...containerStyle
    }
  }, children);
}, (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps));
const inputCss = css`
  .error {
    border: 1px solid #d33e43 !important;
  }

  input:focus,
  input:hover,
  textarea:hover,
  textarea:focus {
    border: 1px solid #0672ab !important;
    color: #0672ab !important;
    box-shadow: 0 2px 3px 0 rgba(34, 36, 38, 0.15) !important;
  }

  input[readonly],
  input:disabled,
  textarea[readonly],
  textarea:disabled {
    cursor: not-allowed;
    border: none !important;
    background-color: #a0b9c8 !important;
    color: #214a61 !important;
  }
`;
/**
 * This component represent an input for text.
 * @param {*} children What will be shown below the input.
 * @param {number} width Div width for the div that contains de input.
 * @param {JSON} style Input styles.
 * @param {*} rest All other supported properties.
 */

export const InputText = ({
  children = null,
  width = 524,
  style = {},
  capitalLetter = false,
  ...rest
}) => {
  const onDidUpdate = useOnDidUpdate();
  const [count, setCount] = useState(0);
  const {
    showCount = false,
    mask = null,
    error,
    touched,
    onChange,
    inputRef: Ref = null
  } = rest; // eslint-disable-next-line no-param-reassign

  delete rest.onChange; // eslint-disable-next-line no-param-reassign

  delete rest.showCount; // eslint-disable-next-line no-param-reassign

  delete rest.touched; // eslint-disable-next-line no-param-reassign

  delete rest.inputRef;
  let inputRef = useRef(null);

  if (Ref) {
    inputRef = Ref;
  }

  const countWords = ({
    target: {
      value
    }
  }) => {
    setCount(value.length);
  };

  useEffect(() => {
    if (mask) {
      const maskProps = {
        autoUnmask: true,
        showMaskOnHover: false,
        ...mask
      }; // this lib cause re-render

      const inputmask = new Inputmask(maskProps);
      inputmask.mask(inputRef.current);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, useDeepCompare([mask]));
  useEffect(() => {
    if (showCount) {
      const {
        value
      } = rest;

      if (value.length === 0 && count > 0) {
        setCount(0);
      }

      if (value.length > 0 && count === 0) {
        setCount(value.length);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [rest.value]);

  const renderCount = () => {
    const {
      maxLength
    } = rest;
    return jsx("span", {
      style: {
        position: 'absolute',
        bottom: 5,
        right: 5,
        fontSize: 'calc(10px + (12 - 10) * ((100vw - 768px) / (1600 - 768)))',
        color: '#0F7CB5'
      }
    }, count, maxLength ? `/${maxLength}` : '');
  };

  function onDidUpdateExec(event, text) {
    const input = event.target;
    const cursor = input.selectionStart;
    const difference = event.target.value.length - (text === null || text === void 0 ? void 0 : text.length) || 0;
    const newCursor = getCursorPosition((text === null || text === void 0 ? void 0 : text.length) ? text : event.target.value, cursor - difference);
    onDidUpdate(() => {
      input.selectionStart = newCursor;
      input.selectionEnd = newCursor;
    });
  }

  const onInputChange = e => {
    const {
      value
    } = e.target;

    if (showCount) {
      countWords(e);
    }

    if (onChange) {
      if (onChange.name.length > 0) {
        const text = onChange(capitalLetter ? value.toUpperCase() : value);

        if (text && !mask) {
          onDidUpdateExec(e, text);
        }

        return;
      } // eslint-disable-next-line no-param-reassign


      e.target.value = capitalLetter ? value.toUpperCase() : value;
      const text = onChange(e);

      if (text) {
        onDidUpdateExec(e, text);
      }
    }
  };

  const cssStyle = {
    backgroundColor: '#FFFFFF',
    border: '1px solid #2b2d42',
    boxShadow: '0px 7px 64px rgba(0, 0, 0, 0.07)',
    borderRadius: 6,
    width,
    height: 50,
    padding: '14px 16px',
    fontSize: 15,
    color: '#2b2d42',
    boxSizing: 'border-box',
    ...style
  };
  return jsx("div", {
    css: inputCss,
    style: {
      display: 'flex',
      flexDirection: 'column'
    }
  }, jsx("div", {
    css: {
      position: 'relative',
      width,
      [mediaQuery[0]]: {
        width: '100% !important'
      }
    }
  }, jsx("input", _extends({
    ref: inputRef,
    onChange: onInputChange,
    type: "text",
    className: error && error.length > 0 && touched ? 'error' : '',
    style: cssStyle
  }, rest)), children || '', showCount ? renderCount() : ''));
};
/**
 * This component represent a Date picker.
 * @param {string} placeholder Value show when is nothing write in the date picker.
 * @param {string} label Description text for the input.
 * @param {function} onChange Function triggered everytime the picker selection changes.
 * @param {string} parentWidth Parent width.
 * @param {JSON} containerStyle Custom style for the date picker container.
 * @param {*} rest All other supported properties.
 */

export const DatePicker = /*#__PURE__*/memo(({
  placeholder,
  label,
  onChange,
  value = '',
  parentWidth = 'unset',
  containerStyle = {},
  ...rest
}) => {
  const {
    error,
    touched
  } = rest;
  return jsx("div", {
    style: {
      display: 'flex',
      flexDirection: 'column'
    }
  }, jsx(FormLabel, {
    text: label
  }), jsx(DatesPicker, _extends({
    placeholder: placeholder,
    onChange: onChange,
    parentWidth: parentWidth,
    value: value,
    containerStyle: {
      backgroundColor: '#FFFFFF',
      border: `1px solid ${error && error.length > 0 && touched ? '#d33e43' : '#2b2d42'}`,
      boxShadow: '0px 7px 64px rgba(0, 0, 0, 0.07)',
      borderRadius: 6,
      width: 524,
      height: 50,
      padding: '14px 16px',
      fontSize: 15,
      color: '#2b2d42',
      ...containerStyle
    }
  }, rest)));
}, (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps));
/**
 * This component represent a Check box.
 * @param {*} children Description text for the check box.
 * @param {string} text Description text for the check box.
 * @param {string} colorChecked Custom styles for checked boxes.
 * @param {string} borderCheckbox Custom styles for checked boxes border.
 * @param {*} rest All other supported properties.
 */

export function CheckBox({
  children,
  text,
  colorChecked,
  borderCheckbox,
  ...rest
}) {
  const backgroundColor = colorChecked || '#1896d8';
  const borderColor = borderCheckbox || '#1896d8';
  const checkBox = css`
    label {
      color: #333333;
      font-size: 12px;
    }

    .checkBox-div {
      position: relative;
      cursor: pointer;
      width: 24px;
      height: 24px;
      margin-right: 11px;
    }

    .checkBox-div input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 100%;
      width: 100%;
      margin: 0;
      z-index: 10;
    }

    .checkmark {
      position: absolute;
      top: 0;
      left: 0;
      height: 18px;
      width: 18px;
      border: 2px solid ${borderColor};
      border-radius: 4px;
    }

    .checkBox-div input:checked ~ .checkmark {
      background-color: ${backgroundColor};
      border: 1px solid ${backgroundColor};
      height: 20px;
      width: 20px;
    }

    .checkmark:after {
      content: '';
      position: absolute;
      display: none;
    }

    .checkBox-div input:checked ~ .checkmark:after {
      display: block;
    }

    .checkBox-div .checkmark:after {
      left: 6px;
      top: 3px;
      width: 6px;
      height: 10px;
      border: 1px solid #fff;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
    .checkBox-div input:disabled ~ .checkmark {
      background-color: #e0e0e0;
      border: 2px solid #666666;
      cursor: not-allowed;
    }

    .checkBox-div input:disabled {
      cursor: not-allowed;
    }
  `;
  return jsx("div", {
    css: checkBox,
    style: {
      display: 'flex',
      flexDirection: 'row'
    }
  }, jsx("div", {
    className: "checkBox-div"
  }, jsx("input", _extends({
    type: "checkbox"
  }, rest)), jsx("span", {
    className: "checkmark"
  })), jsx("div", null, jsx("label", null, children || text)));
}
/**
 * This component represent a label.
 * @param {*} children Label text.
 * @param {string} text Label text.
 * @param {JSON} containerStyle Label custom style.
 */

export const FormLabel = /*#__PURE__*/memo(({
  children,
  text,
  containerStyle,
  customStyle,
  ...props
}) => {
  return jsx("div", {
    className: "formLabel",
    style: {
      marginBottom: 5,
      ...customStyle
    }
  }, jsx("label", _extends({
    style: {
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: 12,
      color: '#333333',
      marginLeft: 15,
      ...containerStyle
    }
  }, props), children || text));
}, (prevProps, nextProps) => JSON.stringify(prevProps) === JSON.stringify(nextProps));
/**
 * This component represent a div with row styles display: 'flex', flexDirection: 'row', flexWrap: 'wrap'.
 * @param {*} children What will be shown inside the div.
 * @param {JSON} containerStyle Custom style for the div.
 * @param {*} props All other supported properties.
 */

export function DivRow({
  children,
  containerStyle,
  ...props
}) {
  return jsx("div", _extends({
    style: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      marginBottom: 23,
      ...containerStyle
    }
  }, props), children);
}
/**
 * This component represent a div with column styles display: 'flex', flexDirection: 'column', flex: 1.
 * @param {*} children What will be shown inside the div.
 * @param {JSON} containerStyle Custom style for the div.
 * @param {*} props All other supported properties.
 */

export function DivColumn({
  children,
  containerStyle,
  ...props
}) {
  return jsx("div", _extends({
    style: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      ...containerStyle
    }
  }, props), children);
}
/**
 * This component represents some space.
 * @param {*} containerStyle Custom div styles.
 */

export const SpaceBar = ({
  containerStyle
}) => jsx("div", {
  css: styles,
  style: { ...containerStyle
  }
});
export function FormDiv({
  children,
  containerStyle
}) {
  return jsx("div", {
    style: {
      marginBottom: 23,
      ...containerStyle
    }
  }, children);
}